import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  pursuitActionsQueryKeys,
  pursuitStageDueDatesQueryKeys,
} from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type {
  PursuitStageDueDate,
  PursuitStageDueDateSet,
} from '@/types/pursuit/StageDueDate'

interface UsePursuitStageDueDatesProps {
  pursuitId: UUID | ''
}

export const usePursuitStageDueDates = ({
  pursuitId,
}: UsePursuitStageDueDatesProps) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: pursuitStageDueDatesQueryKeys.pursuit({ pursuitId }),
    queryFn: () => fetchStageDueDates(pursuitId, accessToken),
    enabled: !!pursuitId,
  })
}

interface UseUpdatePursuitStageDueDatesProps {
  pursuitId: UUID | ''
}

export const useUpdatePursuitStageDueDates = ({
  pursuitId,
}: UseUpdatePursuitStageDueDatesProps) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation({
    mutationFn: (stageDueDates: PursuitStageDueDateSet[]) =>
      updateStageDueDates(pursuitId, stageDueDates, accessToken),
    onSuccess: (data) => {
      queryClient.setQueryData(
        pursuitStageDueDatesQueryKeys.pursuit({ pursuitId }),
        data,
      )
      return queryClient.invalidateQueries({
        queryKey: pursuitActionsQueryKeys.pursuit({ pursuitId }),
      })
    },
    onError: (error) => {
      handleError(error)
    },
  })
}

const fetchStageDueDates = async (
  pursuitId: UUID | '',
  accessToken: string | undefined | null,
): Promise<PursuitStageDueDate[]> => {
  if (!accessToken || !pursuitId) return []

  const res = await fetch(
    `${API_URL}/api/v1/pursuits/${pursuitId}/stage-due-dates`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  const json = await res.json()
  return (json.data as PursuitStageDueDate[]).map((item) =>
    convertDatesInObject(item),
  )
}

const updateStageDueDates = async (
  pursuitId: UUID | '',
  stageDueDates: PursuitStageDueDateSet[],
  accessToken: string | undefined | null,
): Promise<PursuitStageDueDate[]> => {
  if (!accessToken || !pursuitId) return []

  const res = await fetch(
    `${API_URL}/api/v1/pursuits/${pursuitId}/stage-due-dates`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ stage_due_dates: stageDueDates }),
    },
  )

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  const json = await res.json()
  return (json.data as PursuitStageDueDate[]).map((item) =>
    convertDatesInObject(item),
  )
}
